/**
 * "Did you get the …?" in the web app (docs/APP-EVENTS.md; core appevents.ts E5): on Today (the oldest due) or a supply's own page
 * (that item), at least a day after a buy link was opened in this browser, once per tap and at most once a week per item. "Yes, I
 * got it" marks the supply as had (a home: ticks its checklist item) and counts it; "Not yet" counts that. Businesses and homes
 * (owner, 2026-10-07), only while usage sharing is on (the tap had to be counted to be asked about).
 */
import { useEffect, useState } from "react";
import { catalogSupplyKey, dueGotIt, gotItQuestion, isHomeItem, type PendingBuy } from "@logwave/core";
import { isHomeOrg } from "../api.ts";
import { gotItAnswered, gotItAsked, loadGotIt, tracker } from "../lib/appEvents.ts";
import { useApi, useSession } from "../session.tsx";
import { Button, Card } from "./ui.tsx";


export function GotItAsk({ item, now = () => Date.now() }: { item?: string; now?: () => number }) {
  const s = useSession();
  const api = useApi();
  const [ask, setAsk] = useState<PendingBuy | null>(null);
  const [phase, setPhase] = useState<"ask" | "busy" | "yes" | "later">("ask");
  const home = isHomeOrg(s.org);
  useEffect(() => {
    if (!tracker.on) return;
    const t = now(), due = dueGotIt(loadGotIt(), t, item);
    if (!due) return;
    gotItAsked(due.item, t); // shown = asked: once per tap, at most once a week
    setAsk(due);
  }, [item]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ask || phase === "later") return null;
  if (phase === "yes") return <div data-testid="got-it-thanks"><Card className="stack" edge="go"><p>{home ? "Marked as had. It's ticked on your list." : "Marked as had. It's on your supplies."}</p></Card></div>;
  const answer = async (a: "yes" | "not_yet") => {
    setPhase("busy");
    tracker.answer(a === "yes" ? "got_it" : "not_yet", ask.item, ask.placement);
    gotItAnswered(ask.item, a, now());
    if (a === "yes") {
      try {
        // A home ticks its checklist item; a business marks the supply as had.
        if (home) { if (isHomeItem(ask.item)) await api.setHomeHave(s.org.id, ask.item, true); }
        else { const r = await api.putSupply(s.org.id, s.location.id, { key: catalogSupplyKey(ask.item), name: ask.name, stock: "have" }); s.patchLocation(r.location); }
      } catch { /* the answer is kept; it can be marked by hand */ }
    }
    setPhase(a === "yes" ? "yes" : "later");
  };
  return (
    <div data-testid="got-it-ask">
      <Card className="stack">
        <p><strong>{gotItQuestion(ask.name)}</strong></p>
        <div className="row btn-row">
          <Button data-testid="got-it-yes" icon="check" busy={phase === "busy"} onClick={() => void answer("yes")}>I got it</Button>
          <Button data-testid="got-it-not-yet" variant="secondary" disabled={phase === "busy"} onClick={() => void answer("not_yet")}>Not yet</Button>
        </div>
      </Card>
    </div>
  );
}
